import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Checkbox,
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Alert
} from '@mui/material';
import {
  AlertTriangle,
  Briefcase,
  CalendarDays,
  Columns3,
  IdCard,
  Mail,
  Phone,
  RefreshCw,
  Search,
  ShieldAlert,
  Trash2,
  User as UserIcon,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PracticeShareRing } from '../components/pf/PfUsageMetrics';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const labelRighePerPagina = 'Righe per pagina:';

const labelDaA = ({ from, to, count }) => {
  const total = count !== -1 ? count : `più di ${to}`;
  return `${from}-${to} di ${total}`;
};

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('it-IT');
}

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

function calcolaEta(dataNascita) {
  if (!dataNascita) return null;
  const d = new Date(dataNascita);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age -= 1;
  if (!Number.isFinite(age) || age < 0) return null;
  return age;
}

function normalizeBadgeColor(value) {
  return String(value || '').trim().toLowerCase();
}

function isHexColor(value) {
  const s = String(value || '').trim();
  return /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(s);
}

function hexToRgb(hex) {
  const raw = String(hex || '').trim().replace('#', '');
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0] + raw[0], 16),
      g: parseInt(raw[1] + raw[1], 16),
      b: parseInt(raw[2] + raw[2], 16)
    };
  }
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16)
  };
}

function rgba(rgb, alpha) {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function getBadgeStyle(color) {
  const c = normalizeBadgeColor(color);
  if (isHexColor(c)) {
    const rgb = hexToRgb(c);
    return {
      border: rgba(rgb, 0.35),
      bg: rgba(rgb, 0.08),
      fg: c
    };
  }

  const map = {
    blue: { border: 'rgba(59, 130, 246, 0.35)', bg: 'rgba(59, 130, 246, 0.08)', fg: '#2563EB' },
    indigo: { border: 'rgba(99, 102, 241, 0.35)', bg: 'rgba(99, 102, 241, 0.08)', fg: '#4F46E5' },
    amber: { border: 'rgba(245, 158, 11, 0.35)', bg: 'rgba(245, 158, 11, 0.08)', fg: '#D97706' },
    green: { border: 'rgba(16, 185, 129, 0.35)', bg: 'rgba(16, 185, 129, 0.08)', fg: '#059669' },
    teal: { border: 'rgba(20, 184, 166, 0.35)', bg: 'rgba(20, 184, 166, 0.08)', fg: '#0D9488' },
    red: { border: 'rgba(239, 68, 68, 0.35)', bg: 'rgba(239, 68, 68, 0.08)', fg: '#DC2626' },
    gray: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.08)', fg: '#475569' },
    grey: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.08)', fg: '#475569' }
  };

  return map[c] || map.gray;
}

export default function PfClienti() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const definizioneColonne = useMemo(
    () => [
      { key: 'crm_id', label: 'CRM ID', sortable: true, align: 'left' },
      { key: 'cliente', label: 'Cliente', sortable: true, sortKey: 'cognome', align: 'left' },
      { key: 'codice_fiscale', label: 'Codice Fiscale', sortable: true, align: 'left' },
      { key: 'data_nascita', label: 'Data nascita', sortable: true, align: 'left' },
      { key: 'tipo_impiego', label: 'Tipo impiego', sortable: true, align: 'left' },
      { key: 'workflow_progress', label: 'Avanzamento\npratica', sortable: false, align: 'center' },
      { key: 'tipo_pratica', label: 'Tipo pratica', sortable: false, align: 'left' },
      { key: 'fascia_tfs', label: 'Fascia TFS', sortable: true, align: 'left' },
      { key: 'abilitato_da', label: 'Abilitato da', sortable: true, align: 'left' },
      { key: 'data_abilitazione', label: 'Data abilitazione', sortable: true, align: 'left' },
      { key: 'note_cliente', label: 'Note', sortable: false, align: 'left' },
      ...(isAdmin ? [{ key: 'azioni', label: 'Azioni', sortable: false, align: 'center' }] : [])
    ],
    [isAdmin]
  );

  const [clienti, setClienti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState('');

  const [pagina, setPagina] = useState(0);
  const [righePerPagina, setRighePerPagina] = useState(10);
  const [totale, setTotale] = useState(0);

  const [ordine, setOrdine] = useState('asc');
  const [ordinaPer, setOrdinaPer] = useState('cognome');

  const [testoFiltro, setTestoFiltro] = useState('');
  const [filtroAbilitato, setFiltroAbilitato] = useState('');
  const [filtroFasciaTfs, setFiltroFasciaTfs] = useState('');
  const [filtroTipoImpiego, setFiltroTipoImpiego] = useState('');
  const [workflowStati, setWorkflowStati] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [ancoraMenuColonne, setAncoraMenuColonne] = useState(null);
  const [colonneVisibili, setColonneVisibili] = useState([
    'cliente',
    'codice_fiscale',
    'data_nascita',
    'tipo_impiego',
    'workflow_progress',
    'tipo_pratica',
    ...(isAdmin ? ['azioni'] : [])
  ]);

  const colonneMostrate = useMemo(() => definizioneColonne.filter((c) => colonneVisibili.includes(c.key)), [definizioneColonne, colonneVisibili]);

  const caricaClienti = async () => {
    setCaricamento(true);
    try {
      const response = await api.get('/pf-clienti', {
        params: {
          page: pagina + 1,
          limit: righePerPagina,
          search: testoFiltro.trim() || undefined,
          abilitato: filtroAbilitato !== '' ? filtroAbilitato : undefined,
          fascia_tfs: filtroFasciaTfs !== '' ? filtroFasciaTfs : undefined,
          tipo_impiego: filtroTipoImpiego || undefined,
          orderBy: ordinaPer,
          orderDirection: ordine
        }
      });
      setClienti(Array.isArray(response.data?.data) ? response.data.data : []);
      setTotale(Number(response.data?.total || 0));
      setErrore('');
    } catch (err) {
      console.error('Error fetching pf clienti:', err);
      setErrore('Errore nel caricamento dei clienti');
    } finally {
      setCaricamento(false);
    }
  };

  useEffect(() => {
    caricaClienti();
  }, [pagina, righePerPagina, ordine, ordinaPer, filtroAbilitato, filtroFasciaTfs, filtroTipoImpiego]);

  useEffect(() => {
    const caricaWorkflowStati = async () => {
      try {
        const response = await api.get('/pf-stati-pratica');
        setWorkflowStati(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching pf stati pratica for workflow:', err);
      }
    };
    caricaWorkflowStati();
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setPagina(0);
      caricaClienti();
    }, 250);
    return () => clearTimeout(handle);
  }, [testoFiltro]);

  const handleRequestSort = (property) => {
    const isAsc = ordinaPer === property && ordine === 'asc';
    setOrdine(isAsc ? 'desc' : 'asc');
    setOrdinaPer(property);
    setPagina(0);
  };

  const handleChangePage = (_event, newPage) => {
    setPagina(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRighePerPagina(parseInt(event.target.value, 10));
    setPagina(0);
  };

  const handleToggleColumn = (key) => {
    setColonneVisibili((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleOpenDeleteDialog = (row) => {
    setDeleteTarget(row || null);
    setDeleteDialogOpen(true);
    setDeleteImpact(null);
    setDeleteImpactLoading(true);
    api
      .get(`/pf-clienti/${row?.id}/delete-impact`)
      .then((response) => {
        setDeleteImpact(response.data?.impact || null);
      })
      .catch((err) => {
        console.error('Error loading pf cliente delete impact:', err);
        setDeleteImpact(null);
      })
      .finally(() => {
        setDeleteImpactLoading(false);
      });
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
    setDeleteImpact(null);
    setDeleteImpactLoading(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      const response = await api.delete(`/pf-clienti/${deleteTarget.id}`);
      const deletedCount = Number(response?.data?.deleted?.cliente_id ? 1 : 0);
      if (clienti.length === deletedCount && pagina > 0) {
        setPagina((prev) => Math.max(0, prev - 1));
      } else {
        await caricaClienti();
      }
      setErrore('');
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting pf cliente:', err);
      setErrore(err?.response?.data?.message || 'Errore durante l’eliminazione del cliente');
    } finally {
      setDeleteLoading(false);
    }
  };

  const workflowStatiAttivi = useMemo(
    () =>
      (workflowStati || [])
        .filter((row) => !Number(row?.finale))
        .sort((a, b) => Number(a?.ordinamento || 0) - Number(b?.ordinamento || 0)),
    [workflowStati]
  );

  const getWorkflowProgress = (row) => {
    if (!row?.pratica_attiva_id) return null;
    if (!workflowStatiAttivi.length) return null;

    const currentStateId = Number(row.pratica_attiva_stato_id);
    const idxById = workflowStatiAttivi.findIndex((item) => Number(item?.id) === currentStateId);
    const idxByOrder =
      idxById >= 0
        ? idxById
        : workflowStatiAttivi.findIndex(
            (item) => Number(item?.ordinamento || 0) === Number(row.pratica_attiva_stato_ordinamento || -1)
          );

    if (idxByOrder < 0) return null;
    const totalSteps = workflowStatiAttivi.length;
    if (!totalSteps) return null;

    return Math.max(0, Math.min(100, Math.round(((idxByOrder + 1) / totalSteps) * 100)));
  };

  const renderClienteCell = (row) => {
    const fullName = `${row.cognome || ''} ${row.nome || ''}`.trim() || '-';
    const praticaBadge = getBadgeStyle(row.pratica_attiva_stato_colore_badge);
    return (
      <Box sx={{ minWidth: 220, display: 'grid', gap: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <UserIcon size={14} color="#2563eb" />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {fullName}
          </Typography>
        </Box>
        {row.telefono ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Phone size={13} color="#64748b" />
            <Typography variant="caption" color="text.secondary">
              {row.telefono}
            </Typography>
          </Box>
        ) : null}
        {row.email ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Mail size={13} color="#64748b" />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              {row.email}
            </Typography>
          </Box>
        ) : null}
        {row.pratica_attiva_id ? (
          <Box
            sx={{
              mt: 0.5,
              pt: 0.75,
              borderTop: '1px dashed',
              borderColor: 'divider',
              display: 'grid',
              gap: 0.4
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 999,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: praticaBadge.border,
                  bgcolor: praticaBadge.bg,
                  color: praticaBadge.fg
                }}
              >
                {row.pratica_attiva_stato_nome || 'Attiva'}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDate(row.pratica_attiva_data)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCurrency(row.pratica_attiva_importo_richiesto)}
              </Typography>
              <Button
                variant="outline"
                onClick={() => navigate(`/pratiche-finanziarie/pratiche/${row.pratica_attiva_id}`)}
                className="h-6 px-2 text-[11px]"
              >
                Apri pratica
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderWorkflowCell = (row) => {
    const progress = getWorkflowProgress(row);
    if (progress === null) {
      if (!row?.pratica_attiva_id) return '-';
      if (Number(row?.pratica_attiva_stato_finale)) {
        const statoBadge = getBadgeStyle(row.pratica_attiva_stato_colore_badge);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.45 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.35,
                borderRadius: 999,
                border: '1px solid',
                borderColor: statoBadge.border,
                bgcolor: statoBadge.bg,
                color: statoBadge.fg,
                fontSize: '0.72rem',
                fontWeight: 700,
                lineHeight: 1.1
              }}
            >
              {row.pratica_attiva_stato_nome || 'Chiusa'}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.1,
                textAlign: 'center',
                maxWidth: 88,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              Stato finale
            </Typography>
          </Box>
        );
      }
      return '-';
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.45 }}>
        <PracticeShareRing value={progress} />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.1,
            textAlign: 'center',
            maxWidth: 88,
            whiteSpace: 'normal',
            wordBreak: 'break-word'
          }}
        >
          {row.pratica_attiva_stato_nome || '-'}
        </Typography>
      </Box>
    );
  };

  const renderTipoPraticaCell = (row) => {
    if (!row?.pratica_attiva_id) return '-';
    const tipoBadge = getBadgeStyle(row.pratica_attiva_tipo_operazione_colore_badge);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Briefcase size={15} color={tipoBadge.fg} />
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: 0.45,
            borderRadius: 999,
            border: '1px solid',
            borderColor: tipoBadge.border,
            bgcolor: tipoBadge.bg,
            color: tipoBadge.fg,
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: 1.1,
            whiteSpace: 'normal',
            wordBreak: 'break-word'
          }}
        >
          {row.pratica_attiva_tipo_operazione_nome || `Pratica #${row.pratica_attiva_id}`}
        </Box>
      </Box>
    );
  };

  const renderActionsCell = (row) => {
    if (!isAdmin) return '-';
    return (
      <Stack direction="row" spacing={0.25} justifyContent="center">
        <Tooltip title="Elimina cliente PF">
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteDialog(row)}
            sx={{
              color: '#dc2626',
              bgcolor: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.14)',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.14)'
              }
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Box className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <Users className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Pratiche Finanziarie — Clienti
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Elenco clienti (dati alimentati da procedura automatica)
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={caricaClienti} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {errore && (
        <Alert severity="error" className="mb-4">
          {errore}
        </Alert>
      )}

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              value={testoFiltro}
              onChange={(e) => setTestoFiltro(e.target.value)}
              placeholder="Cerca (nome, cognome, CF, email, telefono...)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                )
              }}
              sx={{ width: 360 }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Abilitato</InputLabel>
              <Select
                label="Abilitato"
                value={filtroAbilitato}
                onChange={(e) => {
                  setFiltroAbilitato(e.target.value);
                  setPagina(0);
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="1">Sì</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Fascia TFS</InputLabel>
              <Select
                label="Fascia TFS"
                value={filtroFasciaTfs}
                onChange={(e) => {
                  setFiltroFasciaTfs(e.target.value);
                  setPagina(0);
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="1">Sì</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 210 }}>
              <InputLabel>Tipo impiego</InputLabel>
              <Select
                label="Tipo impiego"
                value={filtroTipoImpiego}
                onChange={(e) => {
                  setFiltroTipoImpiego(e.target.value);
                  setPagina(0);
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="dipendente_pubblico">Dipendente pubblico</MenuItem>
                <MenuItem value="dipendente_privato">Dipendente privato</MenuItem>
                <MenuItem value="pensionato">Pensionato</MenuItem>
                <MenuItem value="altro">Altro</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Tooltip title="Colonne">
            <IconButton onClick={(e) => setAncoraMenuColonne(e.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu anchorEl={ancoraMenuColonne} open={Boolean(ancoraMenuColonne)} onClose={() => setAncoraMenuColonne(null)}>
          {definizioneColonne.map((col) => {
            const checked = colonneVisibili.includes(col.key);
            const disableHide = checked && colonneVisibili.length === 1;
            return (
              <MenuItem key={col.key} dense onClick={() => handleToggleColumn(col.key)} disabled={disableHide}>
                <Checkbox size="small" checked={checked} />
                <Typography variant="body2">{col.label}</Typography>
              </MenuItem>
            );
          })}
        </Menu>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                {colonneMostrate.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sx={{
                      whiteSpace: 'nowrap',
                      minWidth: col.key === 'cliente' ? 220 : col.key === 'workflow_progress' ? 88 : col.key === 'tipo_pratica' ? 180 : undefined
                    }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={ordinaPer === (col.sortKey || col.key)}
                        direction={ordinaPer === (col.sortKey || col.key) ? ordine : 'asc'}
                        onClick={() => handleRequestSort(col.sortKey || col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      <Box component="span" sx={{ whiteSpace: 'pre-line', lineHeight: 1.15, display: 'inline-block', textAlign: 'inherit' }}>
                        {col.label}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {caricamento ? (
                <TableRow>
                  <TableCell colSpan={colonneMostrate.length} align="center" className="py-8">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : clienti.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colonneMostrate.length} align="center" className="py-8 text-gray-500">
                    Nessun cliente trovato
                  </TableCell>
                </TableRow>
              ) : (
                clienti.map((row) => (
                  <TableRow key={row.id} hover>
                    {colonneMostrate.map((col) => (
                      <TableCell
                        key={`${row.id}-${col.key}`}
                        align={col.align}
                        sx={{
                          minWidth:
                            col.key === 'cliente'
                              ? 220
                              : col.key === 'workflow_progress'
                                ? 88
                                : col.key === 'tipo_pratica'
                                  ? 180
                                  : col.key === 'azioni'
                                    ? 92
                                    : undefined
                        }}
                      >
                        {col.key === 'cliente' ? (
                          renderClienteCell(row)
                        ) : col.key === 'workflow_progress' ? (
                          renderWorkflowCell(row)
                        ) : col.key === 'tipo_pratica' ? (
                          renderTipoPraticaCell(row)
                        ) : col.key === 'azioni' ? (
                          renderActionsCell(row)
                        ) : col.key === 'fascia_tfs' ? (
                          <Checkbox checked={Boolean(row.fascia_tfs)} disabled />
                        ) : col.key === 'abilitato_da' ? (
                          row.abilitato_da_nome || row.abilitato_da || '-'
                        ) : col.key === 'data_nascita' ? (
                          (() => {
                            const eta = Number.isFinite(Number(row.eta)) ? Number(row.eta) : calcolaEta(row.data_nascita);
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                  <CalendarDays size={16} color="#2563eb" />
                                  <span>{formatDate(row.data_nascita)}</span>
                                </Box>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                  <UserIcon size={14} color="#dc2626" />
                                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                                    {eta !== null ? `${eta} anni` : '-'}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })()
                        ) : col.key === 'data_abilitazione' ? (
                          formatDateTime(row.data_abilitazione)
                        ) : col.key === 'data_inserimento' ? (
                          formatDateTime(row.data_inserimento)
                        ) : col.key === 'codice_fiscale' ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <IdCard size={16} color="#7c3aed" />
                            <span>{row.codice_fiscale || '-'}</span>
                          </Box>
                        ) : col.key === 'tipo_impiego' ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              <Briefcase size={16} color="#f59e0b" />
                              <span>{row.tipo_impiego || '-'}</span>
                            </Box>
                            {row.professione ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                Professione: {row.professione}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          row[col.key] ?? '-'
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totale}
          rowsPerPage={righePerPagina}
          page={pagina}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={labelRighePerPagina}
          labelDisplayedRows={labelDaA}
        />
      </Card>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Conferma eliminazione cliente PF
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Stai per eliminare definitivamente il cliente PF {deleteTarget ? `"${`${deleteTarget.cognome || ''} ${deleteTarget.nome || ''}`.trim()}"` : ''}.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                L&apos;operazione rimuove il cliente dall&apos;area Pratiche Finanziarie e cancella anche tutti i contenuti collegati.
              </Typography>
            </Box>

            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                p: 2,
                display: 'grid',
                gap: 1.2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(37, 99, 235, 0.10)',
                    color: '#2563eb',
                    flexShrink: 0
                  }}
                >
                  <Users size={16} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Cliente PF
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verranno rimossi anagrafica PF, abilitazione e note operative del cliente selezionato.
                  </Typography>
                  {!deleteImpactLoading ? (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: 'text.secondary' }}>
                      {`Record coinvolti: 1 cliente`}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(99, 102, 241, 0.10)',
                    color: '#4f46e5',
                    flexShrink: 0
                  }}
                >
                  <Briefcase size={16} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Pratiche e storico
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verranno eliminate tutte le pratiche finanziarie collegate, insieme a storico decisioni e transizioni operative.
                  </Typography>
                  {!deleteImpactLoading ? (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: 'text.secondary' }}>
                      {`${Number(deleteImpact?.pratiche || 0)} pratiche · ${Number(deleteImpact?.storico_azioni || 0)} eventi storico`}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(217, 119, 6, 0.10)',
                    color: '#d97706',
                    flexShrink: 0
                  }}
                >
                  <ShieldAlert size={16} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Documenti e file collegati
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verranno rimossi checklist documentale, record documenti e i relativi file caricati sullo storage.
                  </Typography>
                  {!deleteImpactLoading ? (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: 'text.secondary' }}>
                      {`${Number(deleteImpact?.documenti || 0)} documenti · ${Number(deleteImpact?.file_storage || 0)} file storage`}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>

            {deleteImpactLoading ? (
              <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                Sto calcolando il riepilogo reale degli elementi che verranno rimossi.
              </Alert>
            ) : null}

            <Alert
              severity="warning"
              icon={<AlertTriangle size={18} color="#d97706" />}
              sx={{ alignItems: 'flex-start' }}
            >
              Questa azione è irreversibile. Dopo la conferma non sarà più possibile recuperare il cliente PF né gli elementi collegati.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outline" onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading} className="gap-2">
            <Trash2 className="h-4 w-4" />
            {deleteLoading ? 'Eliminazione...' : 'Conferma eliminazione'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
